import React, { useState, useEffect } from 'react';
import { useSocket } from '../../context/SocketContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { 
  Clock, 
  User, 
  MapPin, 
  AlertTriangle,
  CheckCircle,
  X,
  Loader
} from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';

const ScanWaiting = ({ scanId, onClose }) => {
  const { socket, joinScanRoom, removeConsentRequest } = useSocket();
  const { user } = useAuth();
  const [countdown, setCountdown] = useState(60);
  const [consentStatus, setConsentStatus] = useState('pending');
  const [location, setLocation] = useState(null);

  useEffect(() => {
    if (socket && scanId) {
      joinScanRoom(scanId);
    }

    // Get current location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.warn('Location access denied:', error);
        }
      );
    }
  }, [socket, scanId, joinScanRoom]);

  useEffect(() => {
    if (consentStatus === 'pending' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && consentStatus === 'pending') {
      setConsentStatus('auto-approved');
    }
  }, [countdown, consentStatus]);

  useEffect(() => {
    if (socket) {
      const handleConsentResult = (data) => {
        console.log('Consent result received:', data);
        if (data.approved) {
          setConsentStatus(data.auto ? 'auto-approved' : 'approved');
          if (data.auto) {
            setTimeout(() => {
              window.location.href = `/emergency/${scanId}?scanId=${scanId}`;
            }, 2000);
          } else {
            setTimeout(() => {
              window.location.href = `/emergency/${scanId}?scanId=${scanId}`;
            }, 2000);
          }
        } else {
          setConsentStatus('denied');
          setTimeout(() => {
            onClose();
          }, 3000);
        }
      };

      socket.on('consent_result', handleConsentResult);

      return () => {
        socket.off('consent_result', handleConsentResult);
      };
    }
  }, [socket, scanId, onClose]);

  const getStatusColor = () => {
    switch (consentStatus) {
      case 'pending':
        return 'text-yellow-600';
      case 'approved':
      case 'auto-approved':
        return 'text-green-600';
      case 'denied':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusIcon = () => {
    switch (consentStatus) {
      case 'pending':
        return <Clock className="h-8 w-8 animate-pulse" />;
      case 'approved':
        return <CheckCircle className="h-8 w-8" />;
      case 'auto-approved':
        return <CheckCircle className="h-8 w-8" />;
      case 'denied':
        return <AlertTriangle className="h-8 w-8" />;
      default:
        return <Loader className="h-8 w-8" />;
    }
  };

  const getStatusText = () => {
    switch (consentStatus) {
      case 'pending':
        return 'Waiting for consent...';
      case 'approved':
        return 'Access granted!';
      case 'auto-approved':
        return 'Auto-approved (60s timeout)';
      case 'denied':
        return 'Access denied';
      default:
        return 'Processing...';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            Emergency Access Request
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Status */}
          <div className="text-center mb-6">
            <div className={`${getStatusColor()} mb-4`}>
              {getStatusIcon()}
            </div>
            <h4 className={`text-xl font-semibold ${getStatusColor()}`}>
              {getStatusText()}
            </h4>
            {consentStatus === 'pending' && (
              <p className="text-gray-600 mt-2">
                The card owner has been notified and can grant or deny access
              </p>
            )}
          </div>

          {/* Countdown Timer */}
          {consentStatus === 'pending' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-600 mb-2">
                  {countdown}s
                </div>
                <p className="text-yellow-700 text-sm">
                  Auto-approval if no response
                </p>
              </div>
            </div>
          )}

          {/* Request Details */}
          <div className="space-y-4 mb-6">
            <div className="flex items-center space-x-3">
              <User className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">Requester</p>
                <p className="font-medium">Emergency Medical Service</p>
              </div>
            </div>

            {location && (
              <div className="flex items-center space-x-3">
                <MapPin className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Location</p>
                  <p className="font-medium">
                    {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center space-x-3">
              <Clock className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">Request Time</p>
                <p className="font-medium">
                  {new Date().toLocaleTimeString()}
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {consentStatus === 'pending' && (
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="flex-1 btn-secondary"
              >
                Cancel Request
              </button>
              <button
                disabled
                className="flex-1 btn-primary opacity-50 cursor-not-allowed"
              >
                <LoadingSpinner size="small" />
                Waiting...
              </button>
            </div>
          )}

          {consentStatus === 'approved' && (
            <button
              onClick={() => window.location.href = `/emergency/${scanId}?scanId=${scanId}`}
              className="w-full btn-success"
            >
              View Medical Profile
            </button>
          )}

          {consentStatus === 'auto-approved' && (
            <div className="text-center">
              <LoadingSpinner size="medium" />
              <p className="text-gray-600 mt-2">
                Redirecting to emergency profile...
              </p>
            </div>
          )}

          {consentStatus === 'denied' && (
            <div className="text-center">
              <p className="text-red-600 mb-4">
                Access was denied by the card owner
              </p>
              <button
                onClick={onClose}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          )}
        </div>

        {/* Footer Notice */}
        <div className="bg-gray-50 px-6 py-4 border-t">
          <p className="text-xs text-gray-500 text-center">
            This request is logged and tracked for security purposes
          </p>
        </div>
      </div>
    </div>
  );
};

export default ScanWaiting;
